import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from '../../services/api/axiosBaseQuery';
import {
  GetReportsActivitiesPayload,
  ReportsActivitiesResponse,
} from '../../models/reports';
import { ReportsApiURL } from '../../services/api/reportsServices';
import { Activity, ActivityPayload } from '../../models/activity';
import { ActivitiesApiURL } from '../../services/api/activityServices';
import { convertMinutesToHours } from '../../services/conversionService';

export const reportsActivityQueryTag = 'ReportsActivity';

export const reportsActivitiesService = createApi({
  reducerPath: 'reportsActivitiesService',
  baseQuery: axiosBaseQuery(),
  tagTypes: [reportsActivityQueryTag],

  endpoints: (builder) => ({
    getReportsActivities: builder.query<
      Activity[],
      GetReportsActivitiesPayload
    >({
      query: (payload: GetReportsActivitiesPayload) => ({
        url: `${ReportsApiURL}/activities`,
        method: 'POST',
        data: payload,
      }),

      transformResponse: (result: ReportsActivitiesResponse[]) => {
        const processedResponse = result
          .map((item) =>
            item.activities.map((activity) => ({
              ...activity,
              hours: convertMinutesToHours(activity.minutes),
            }))
          )
          .flat();

        return processedResponse;
      },

      providesTags: (result, error, { employeeId, startDate, endDate }) => [
        {
          type: reportsActivityQueryTag,
          id: `${employeeId}-${startDate}-${endDate}`,
        },
      ],
      keepUnusedDataFor: 30,
    }),

    addReportsActivity: builder.mutation<void, ActivityPayload>({
      query: (payload: ActivityPayload) => ({
        url: ActivitiesApiURL,
        method: 'POST',
        data: {
          ...payload,
          hours: convertMinutesToHours(payload.minutes),
        },
      }),
      invalidatesTags: [reportsActivityQueryTag],
    }),

    editReportsActivity: builder.mutation<
      void,
      {
        idActivity: string;
        editActivityInfo: ActivityPayload;
      }
    >({
      query: ({ idActivity, editActivityInfo }) => ({
        url: `${ActivitiesApiURL}/${idActivity}`,
        method: 'PUT',
        data: editActivityInfo,
      }),
      invalidatesTags: [reportsActivityQueryTag],
    }),
    deleteReportsActivity: builder.mutation<void, string>({
      query: (idActivity: string) => ({
        url: `${ActivitiesApiURL}/${idActivity}`,
        method: 'DELETE',
      }),
      invalidatesTags: [reportsActivityQueryTag],
    }),
  }),
});

export const {
  useGetReportsActivitiesQuery,
  useAddReportsActivityMutation,
  useEditReportsActivityMutation,
  useDeleteReportsActivityMutation,
} = reportsActivitiesService;
